// @ts-nocheck
import { useEffect, useState } from "react";
import { useApp } from "@/context/AppCtx";
import { Play, Rocket, Loader2, Plus, Trash2, Twitter } from "lucide-react";
import type { AuditResult, LiveResult } from "@/lib/algo/api";
import type { LaunchSummary } from "@/lib/algo/apiLive";
import { buildPlan } from "@/lib/algo/plan";
import type { FlowGraph, LaunchCoinType, RewardMode } from "@/lib/algo/graph";
import type { Validation } from "@/lib/algo/validate";
import { LaunchChecklist } from "@/components/algo/LaunchChecklist";

const COIN_TYPES: { v: LaunchCoinType; label: string }[] = [
  { v: "pump-sol", label: "Pump · SOL" }, { v: "pump-usdc", label: "Pump · USDC" },
  { v: "cashback", label: "Cashback" }, { v: "charity", label: "Charity" }, { v: "agent", label: "Agent" },
];
const REWARD_MODES: { v: RewardMode; label: string }[] = [
  { v: "creator", label: "Creator (default)" }, { v: "wallet", label: "Wallet" },
  { v: "github", label: "GitHub" }, { v: "charity", label: "Charity" }, { v: "split", label: "Split" },
];

export function LaunchPanel({
  graph, update, validation, live,
}: {
  graph: FlowGraph;
  update: (fn: (d: FlowGraph) => void) => void;
  validation: Validation;
  live: boolean;
}) {
  const canLive = import.meta.env.DEV && live; // statically false in prod → execute UI tree-shaken
  const { addAppToast } = useApp() as any;
  const [busy, setBusy] = useState<"" | "audit" | "live">("");
  const [auditRes, setAuditRes] = useState<AuditResult | null>(null);
  const [liveRes, setLiveRes] = useState<LiveResult | null>(null);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState<LaunchSummary[]>([]);
  const [openId, setOpenId] = useState<string>("");

  const L = graph.launch;
  const patch = (fn: (l: FlowGraph["launch"]) => void) => update((d) => fn(d.launch));
  const blockIssues = validation.issues.filter((i) => i.severity === "block");
  const warnIssues = validation.issues.filter((i) => i.severity === "warn");
  const invalid = validation.severity === "block";

  async function loadHistory() {
    if (!import.meta.env.DEV || !live) return;
    try { const { apiLive } = await import("@/lib/algo/apiLive"); setHistory((await apiLive.launches()).launches); } catch { /* offline */ }
  }
  useEffect(() => { void loadHistory(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [live]);

  function onImage(file: File) {
    if (!file.type.startsWith("image/")) { addAppToast("Pick an image file.", "error"); return; }
    if (file.size > 2 * 1024 * 1024) { addAppToast("Image must be under 2 MB.", "error"); return; }
    const r = new FileReader();
    r.onload = () => patch((l) => { l.imageDataUrl = String(r.result); });
    r.readAsDataURL(file);
  }

  async function run(mode: "audit" | "live") {
    if (!import.meta.env.DEV) return;
    setErr(""); setBusy(mode);
    if (mode === "audit") setAuditRes(null); else setLiveRes(null);
    try {
      const plan = buildPlan(graph);
      const { apiLive } = await import("@/lib/algo/apiLive");
      if (mode === "audit") setAuditRes(await apiLive.audit(plan));
      else { setLiveRes(await apiLive.executeLiveJob(plan, crypto.randomUUID())); void loadHistory(); }
    } catch (e) { setErr(e instanceof Error ? e.message : "failed"); }
    finally { setBusy(""); }
  }

  return (
    <div className="launch-panel">
      <div className="lp-grid">
        {/* ---- left: token + reward config ---- */}
        <div className="lp-col">
          <h3 className="lp-h">Token</h3>
          <div className="grid2">
            <div><label className="lbl">Name</label><input className="field" value={L.name} onChange={(e) => patch((l) => { l.name = e.target.value; })} placeholder="Lily" /></div>
            <div><label className="lbl">Ticker</label><input className="field" value={L.symbol} onChange={(e) => patch((l) => { l.symbol = e.target.value.toUpperCase(); })} placeholder="LILY" /></div>
          </div>
          <div className="grid2" style={{ marginTop: 8 }}>
            <div><label className="lbl">Image</label><input className="field" type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) onImage(f); }} /></div>
            <div><label className="lbl">Coin type</label><select className="field" value={L.coinType} onChange={(e) => patch((l) => { l.coinType = e.target.value as LaunchCoinType; })}>{COIN_TYPES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}</select></div>
          </div>
          <div style={{ marginTop: 8 }}><label className="lbl">Description</label><input className="field" value={L.description ?? ""} onChange={(e) => patch((l) => { l.description = e.target.value; })} /></div>
          <div className="grid3" style={{ marginTop: 8 }}>
            <div><label className="lbl">Website</label><input className="field" value={L.website ?? ""} onChange={(e) => patch((l) => { l.website = e.target.value; })} /></div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 2 }}>
                <label className="lbl" style={{ marginBottom: 0 }}>Twitter</label>
                <button 
                  onClick={() => window.open('/api/auth/twitter', '_blank', 'width=600,height=700')}
                  style={{ background: 'none', border: 'none', color: '#1DA1F2', fontSize: '9px', fontWeight: 'bold', textTransform: 'uppercase', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0 }}
                >
                  <Twitter size={10} /> Connect X
                </button>
              </div>
              <input className="field" value={L.twitter ?? ""} onChange={(e) => patch((l) => { l.twitter = e.target.value; })} placeholder="https://x.com/..." />
            </div>
            <div><label className="lbl">Telegram</label><input className="field" value={L.telegram ?? ""} onChange={(e) => patch((l) => { l.telegram = e.target.value; })} /></div>
          </div>

          <h3 className="lp-h" style={{ marginTop: 16 }}>Creator rewards</h3>
          <div className="grid2">
            <div><label className="lbl">Reward mode</label><select className="field" value={L.rewardMode} onChange={(e) => patch((l) => { l.rewardMode = e.target.value as RewardMode; })}>{REWARD_MODES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}</select></div>
            <div>
              {L.rewardMode === "wallet" && <><label className="lbl">Reward wallet</label><input className="field" value={L.rewardWallet ?? ""} onChange={(e) => patch((l) => { l.rewardWallet = e.target.value; })} placeholder="address" /></>}
              {L.rewardMode === "github" && <><label className="lbl">GitHub user id</label><input className="field" value={L.rewardGithubUserId ?? ""} onChange={(e) => patch((l) => { l.rewardGithubUserId = e.target.value; })} placeholder="e.g. 1024025" /></>}
              {L.rewardMode === "charity" && <><label className="lbl">Charity config id</label><input className="field" value={L.rewardCharityConfigId ?? ""} onChange={(e) => patch((l) => { l.rewardCharityConfigId = e.target.value; })} /></>}
            </div>
          </div>
          {L.rewardMode === "split" && (
            <div style={{ marginTop: 8 }}>
              <label className="lbl">Split shareholders (wallet · %)</label>
              {(L.rewardSplit ?? []).map((s, i) => (
                <div key={i} className="row" style={{ marginBottom: 4 }}>
                  <input className="field" style={{ flex: 1 }} value={s.wallet} placeholder="wallet address" onChange={(e) => patch((l) => { (l.rewardSplit ??= [])[i].wallet = e.target.value; })} />
                  <input className="field" style={{ width: 64 }} type="number" value={s.pct} onChange={(e) => patch((l) => { (l.rewardSplit ??= [])[i].pct = Number(e.target.value) || 0; })} />
                  <button className="icon-btn" onClick={() => patch((l) => { l.rewardSplit = (l.rewardSplit ?? []).filter((_, k) => k !== i); })}><Trash2 size={13} /></button>
                </div>
              ))}
              <button className="btn btn-sm" onClick={() => patch((l) => { (l.rewardSplit ??= []).push({ wallet: "", pct: 0 }); })}><Plus size={12} /> Add shareholder</button>
            </div>
          )}
        </div>

        {/* ---- right: execution params + run + status ---- */}
        <div className="lp-col">
          <h3 className="lp-h">Execution</h3>
          <div className="grid2">
            <div><label className="lbl">Dev wallet</label><select className="field" value={L.devWalletId ?? ""} onChange={(e) => patch((l) => { l.devWalletId = e.target.value || undefined; })}><option value="">Auto (first wallet)</option>{graph.wallets.map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}</select></div>
            <div><label className="lbl">Dev buy (SOL)</label><input className="field" type="number" min="0" step="0.01" value={graph.devBuySol} onChange={(e) => update((d) => { d.devBuySol = Math.max(0, Number(e.target.value) || 0); })} /></div>
          </div>
          <div className="grid2" style={{ marginTop: 8 }}>
            <div><label className="lbl">Slippage %</label><input className="field" type="number" min="0" max="100" value={L.slippage} onChange={(e) => patch((l) => { l.slippage = Math.max(0, Math.min(100, Number(e.target.value) || 0)); })} /></div>
            <div><label className="lbl">Priority (SOL)</label><input className="field" type="number" min="0" step="0.0001" value={L.priorityFee} onChange={(e) => patch((l) => { l.priorityFee = Math.max(0, Number(e.target.value) || 0); })} /></div>
          </div>

          {(blockIssues.length > 0 || warnIssues.length > 0) && (
            <div className="audit" style={{ marginTop: 12 }}>
              <span className={`live-tag ${invalid ? "off" : ""}`} style={invalid ? {} : { color: "#9a7b34", background: "rgba(217,162,74,0.16)" }}>{invalid ? `${blockIssues.length} BLOCKED` : `${warnIssues.length} WARNINGS`}</span>
              {[...blockIssues, ...warnIssues].slice(0, 6).map((i, idx) => <div key={idx} className="small" style={{ marginTop: 4 }}>{i.severity === "block" ? "⛔" : "⚠"} {i.message}</div>)}
            </div>
          )}

          <div className="row" style={{ marginTop: 14 }}>
            {canLive ? (
              <>
                <button className="btn" disabled={busy !== "" || invalid} onClick={() => run("audit")}>{busy === "audit" ? <Loader2 size={13} className="spin" /> : <Play size={13} />} Dry run</button>
                <button className="btn btn-ink" disabled={busy !== "" || invalid || !auditRes?.ok} title={!auditRes?.ok ? "Run a passing dry run first" : "Execute on-chain"} onClick={() => { if (confirm("Execute this launch ON-CHAIN with real SOL?")) run("live"); }}>{busy === "live" ? <Loader2 size={13} className="spin" /> : <Rocket size={13} />} Execute live</button>
              </>
            ) : <span className="small muted">Simulation mode — switch to Alpha to launch on-chain.</span>}
          </div>
          {err && <div className="errs" style={{ marginTop: 8 }}><div>⚠ {err}</div></div>}
          {auditRes && (
            <div className="audit" style={{ marginTop: 10 }}>
              <div><span className={`live-tag ${auditRes.ok ? "on" : "off"}`}>{auditRes.ok ? "PLAN OK" : "BLOCKED"}</span> <span className="small">need <b>{auditRes.totalNeeded.toFixed(3)} SOL</b> · funding {auditRes.fundingOk ? "✓" : "✗"}</span></div>
              {!auditRes.fundingOk && (
                <div style={{ marginTop: 6 }}>
                  {auditRes.fundingChecks.filter((c) => !c.ok).map((c) => (
                    <div key={c.walletId} className="cl-row bad" style={{ fontSize: 11 }}>
                      <span className="cl-icon">✗</span>
                      <span className="cl-label">{c.label} short</span>
                      <span className="cl-detail">need {c.needSol.toFixed(3)} · has {c.haveSol.toFixed(3)} SOL</span>
                    </div>
                  ))}
                  <div className="small muted" style={{ marginTop: 4 }}>→ open Wallets and click <b>Fund all wallets</b>, then re-run Dry run.</div>
                </div>
              )}
            </div>
          )}

          {(busy === "live" || liveRes) && (
            <div style={{ marginTop: 12 }}>
              <LaunchChecklist log={(liveRes?.log ?? []) as never} mint={liveRes?.mint} running={busy === "live"} ok={liveRes ? liveRes.ok : null} />
            </div>
          )}
        </div>
      </div>

      {/* ---- launch history ---- */}
      {canLive && (
        <div className="lp-history">
          <h3 className="lp-h">Launch history <span className="small muted">({history.length})</span></h3>
          {history.length === 0 && <div className="small muted">No launches yet.</div>}
          {history.map((h) => (
            <div key={h.id} className="lh-item">
              <button className="lh-row" onClick={() => setOpenId(openId === h.id ? "" : h.id)}>
                <span className={`live-tag ${h.ok === false ? "off" : h.ok ? "on" : ""}`}>{h.status === "running" ? "…" : h.ok === false ? "✗" : "✓"}</span>
                <span className="lh-name">{h.name || "—"} {h.symbol && <span className="muted">{h.symbol}</span>}</span>
                <span className="small muted">{h.mint ? h.mint.slice(0, 6) + "…" : "no mint"}</span>
                <span className="small muted">{new Date(h.startedAt).toLocaleString()}</span>
              </button>
              {openId === h.id && <LaunchChecklist log={h.log} mint={h.mint} ok={h.ok} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
